import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { softDelete } from '../utils/softDelete';
import { useEsAdmin } from '../UserContext';
import { localToday } from '../utils/dateUtils';

const colorTipo = (tipo) => {
  const map = {
    'Audiencia': '#ef4444', 'Consulta / Turno': '#3b82f6', 'Reunión': '#8b5cf6',
    'Vencimiento procesal': '#f59e0b', 'Vencimiento de deuda': '#f59e0b',
  };
  return map[tipo] || '#9ca3af';
};
const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : null;

const ESTADO_CFG = {
  pendiente: { label: 'Pendiente', dot: '#94a3b8', color: '#64748b', bg: 'var(--surface2)', border: 'var(--border)' },
  realizado: { label: '✓ Realizado', dot: '#16a34a', color: '#15803d', bg: '#dcfce7', border: '#bbf7d0' },
  cancelado: { label: 'Cancelado', dot: '#ef4444', color: '#b91c1c', bg: '#fee2e2', border: '#fecaca' },
};

export default function EventoDetalle({ eventoId, onClose, onEdit, onNavigateExpediente, onNavigateTramite, onNavigateCliente, onChanged }) {
  const esAdmin = useEsAdmin();
  const [evento, setEvento] = useState(null);
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState('');
  const [actualizando, setActualizando] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 4000); };

  useEffect(() => {
    if (eventoId) { fetchEvento(); fetchNotas(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  async function fetchEvento() {
    const { data } = await supabase
      .from('agenda')
      .select('*, clientes(nombre, apellido), expedientes(id, caratula, numero), tramites(id, nombre)')
      .eq('id', eventoId)
      .single();
    setEvento(data);
  }

  async function fetchNotas() {
    const { data } = await supabase
      .from('agenda_notas').select('*')
      .eq('agenda_id', eventoId)
      .order('created_at', { ascending: false });
    setNotas(data || []);
  }

  async function addNota() {
    if (!nuevaNota.trim()) return;
    const { error } = await supabase.from('agenda_notas').insert({ agenda_id: eventoId, contenido: nuevaNota.trim() });
    if (error) { showToast('Error al guardar nota: ' + error.message); return; }
    setNuevaNota('');
    fetchNotas();
  }

  async function deleteNota(id) {
    await softDelete('agenda_notas', id);
    setNotas(ns => ns.filter(n => n.id !== id));
  }

  async function cambiarEstado(estado) {
    setActualizando(true);
    await supabase.from('agenda').update({ estado }).eq('id', eventoId);
    setEvento(prev => ({ ...prev, estado }));
    setActualizando(false);
    onChanged?.();
  }

  if (!evento) return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ width: 520, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
        <span style={{ color: 'var(--text4)', fontSize: 13 }}>Cargando...</span>
      </div>
    </div>
  );

  const color = colorTipo(evento.tipo);
  const estado = ESTADO_CFG[evento.estado] || ESTADO_CFG.pendiente;
  const cliente = evento.clientes ? `${evento.clientes.apellido || ''} ${evento.clientes.nombre || ''}`.trim() : evento.cliente_manual;

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      {toast && <div className="toast" style={{ zIndex: 1200 }}>{toast}</div>}
      <div className="modal" style={{ width: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <div style={{ width: 4, height: 28, borderRadius: 2, background: color, flexShrink: 0 }} />
            <div className="modal-title" style={{ fontSize: 15 }}>{evento.titulo}</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

          {/* Badge estado + tipo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: estado.bg, border: `1px solid ${estado.border}` }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: estado.dot }} />
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: estado.color }}>{estado.label}</span>
            <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color, background: color + '18', padding: '2px 8px', borderRadius: 10 }}>{evento.tipo}</span>
            {evento.prioridad === 'urgente' && (
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 0.5 }}>· URGENTE</span>
            )}
          </div>

          {/* Fecha / hora */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: '#eff6ff', color: '#3b82f6', border: '0.5px solid #bfdbfe', fontWeight: 500, textTransform: 'capitalize' }}>
              📅 {formatDate(evento.fecha)}
            </span>
            {evento.hora && (
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'var(--surface2)', color: 'var(--text2)', border: '0.5px solid var(--border)', fontWeight: 500, fontFamily: 'DM Mono' }}>
                🕐 {evento.hora.substring(0, 5)}
              </span>
            )}
          </div>

          {/* Descripción (campo notas del evento) */}
          {evento.notas && (
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 14, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, whiteSpace: 'pre-wrap' }}>
              {evento.notas}
            </div>
          )}

          {/* Cliente */}
          {cliente && (
            <div
              onClick={onNavigateCliente && evento.cliente_id ? () => { onNavigateCliente(evento.cliente_id); onClose(); } : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 10, cursor: onNavigateCliente && evento.cliente_id ? 'pointer' : 'default' }}
            >
              <span style={{ fontSize: 16 }}>👤</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Cliente</div>
                <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{cliente}</div>
              </div>
              {onNavigateCliente && evento.cliente_id && <span style={{ marginLeft: 'auto', color: 'var(--text3)', fontSize: 16 }}>→</span>}
            </div>
          )}

          {/* Expediente */}
          {evento.expedientes && (
            <div
              onClick={() => { onNavigateExpediente?.(evento.expedientes.id); onClose(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--blue-light)', border: '0.5px solid #bfdbfe', borderRadius: 8, marginBottom: 10, cursor: onNavigateExpediente ? 'pointer' : 'default' }}
            >
              <span style={{ fontSize: 16 }}>📁</span>
              <div>
                <div style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Expediente</div>
                <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
                  {evento.expedientes.numero ? `${evento.expedientes.numero} — ` : ''}{evento.expedientes.caratula}
                </div>
              </div>
              {onNavigateExpediente && <span style={{ marginLeft: 'auto', color: 'var(--blue)', fontSize: 16 }}>→</span>}
            </div>
          )}

          {/* Trámite */}
          {evento.tramites && (
            <div
              onClick={() => { onNavigateTramite?.(evento.tramites.id); onClose(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f3e8ff', border: '0.5px solid #ddd6fe', borderRadius: 8, marginBottom: 10, cursor: onNavigateTramite ? 'pointer' : 'default' }}
            >
              <span style={{ fontSize: 16 }}>📋</span>
              <div>
                <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Trámite</div>
                <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{evento.tramites.nombre}</div>
              </div>
              {onNavigateTramite && <span style={{ marginLeft: 'auto', color: '#7c3aed', fontSize: 16 }}>→</span>}
            </div>
          )}

          {/* Notas (historial) */}
          <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Notas</div>
            <div style={{ marginBottom: 10, position: 'relative', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
              <textarea
                className="form-textarea"
                value={nuevaNota}
                onChange={e => setNuevaNota(e.target.value)}
                placeholder="Agregar una nota..."
                style={{ minHeight: 70, width: '100%', border: 'none', resize: 'none', background: 'transparent', display: 'block', paddingBottom: 38 }}
              />
              <button
                onClick={addNota}
                disabled={!nuevaNota.trim()}
                style={{ position: 'absolute', bottom: 8, right: 8, background: nuevaNota.trim() ? 'var(--blue)' : 'var(--text4)', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 500, cursor: nuevaNota.trim() ? 'pointer' : 'default' }}
              >
                Agregar
              </button>
            </div>
            {notas.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text4)', textAlign: 'center', padding: '10px 0' }}>Sin notas aún</div>
            ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface2)', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {notas.map(n => (
                  <div key={n.id} style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 7, padding: '8px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 10, color: 'var(--text4)', fontFamily: 'DM Mono' }}>
                        {new Date(n.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {esAdmin && <button className="btn btn-danger btn-xs" style={{ padding: '0px 5px' }} onClick={() => deleteNota(n.id)}>🗑</button>}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{n.contenido}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {onEdit && <button className="btn btn-ghost" onClick={() => { onEdit(evento); onClose(); }}>Editar</button>}
            {evento.estado === 'pendiente' && evento.fecha && evento.fecha <= localToday() && (
              <>
                <button
                  onClick={() => { cambiarEstado('cancelado'); onClose(); }}
                  disabled={actualizando}
                  style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                >
                  Se canceló
                </button>
                <button
                  onClick={() => { cambiarEstado('realizado'); onClose(); }}
                  disabled={actualizando}
                  style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                >
                  {actualizando ? 'Guardando...' : 'Se realizó'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
